import styled from 'styled-components/macro';
import {useState} from "react";
import {HtmlRenderer, Parser} from 'commonmark';
import {useFeedback} from "../store/feedback";
import {useEditComment, useEditPost, useRawContent} from "../api/edit";
import {useActions} from "../store";
import MarkdownField from "./markdown_field";
import Button from "./button";
import Spinner from "./spinner";
import ErrorMessage from "./error_message";
import {themecolors} from "../utils/themecolors";

const Save = styled.div``;

const Edit = styled(({className, type, loading, saving, content, setContent, reason, setReason, onSave}) => (
    <div className={className}>
        <MarkdownField
            id='content'
            placeholder={loading.busy ? `Loading your ${type}...` : `Add your ${type}`}
            minRows={5}
            value={content} onChange={setContent} autofocus
            readOnly={loading.busy}
        />
        <ErrorMessage error={loading.error}/>
        <Save>
            {saving.busy && <Spinner/>}
            <Button onClick={onSave}>Save</Button>
        </Save>
        <ErrorMessage error={saving.error}/>
    </div>
))`
  margin-top: 8px;
  max-width: 464px;

  ${Save} {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 12px;
    font-size: 14px;
  }
  ${Save} button {
    margin-left: 12px;
    padding: 8px 16px;
    border-radius: 48px;
    ${({theme}) => `
        color: ${theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
        background-color: ${theme.color.name === 'dark' ? theme.dark_border : '#ececec'};
    `}
  }

  @media only screen and (max-width: 780px) {
    & {
      padding-left: 8px;
      margin-left: 0;
      width: 100%;
    }
  }
`;

export const EditPost = styled(function EditPost({className, post, onClose}) {
    const actions = useActions();
    const [content, setContent] = useState('');
    const [reason, setReason] = useState('');
    const loading = useFeedback();
    useRawContent(loading.channel, post.id, 'post', setContent);
    const saving = useFeedback();
    const editPost = useEditPost(saving.channel);
    const handleSave = async () => {
        const parser = new Parser();
        const renderer = new HtmlRenderer();
        const html = renderer.render(parser.parse(content));
        const action = actions.postEdited(post.id, html);
        try {
            const response = await editPost(post.id, content, reason);
            actions.postEdited(post.id, response.content);
            onClose();
        } catch {
            action.undo();
        }
    };
    return (
        <Edit
            className={className}
            type="post"
            loading={loading}
            saving={saving}
            content={content} setContent={setContent}
            reason={reason} setReason={setReason}
            onSave={handleSave}
        />
    );
})``;

export const EditComment = styled(function EditComment({className, comment, onClose}) {
    const actions = useActions();
    const [content, setContent] = useState('');
    const [reason, setReason] = useState('');
    const loading = useFeedback();
    useRawContent(loading.channel, comment.id, 'comment', setContent);
    const saving = useFeedback();
    const editComment = useEditComment(saving.channel);
    const handleSave = async () => {
        const parser = new Parser();
        const renderer = new HtmlRenderer();
        const html = renderer.render(parser.parse(content));
        const action = actions.commentEdited(comment.id, html);
        try {
            const response = await editComment(comment.id, content, reason);
            actions.commentEdited(comment.id, response.content);
            onClose();
        } catch {
            action.undo();
        }
    };
    return (
        <Edit
            className={className}
            type="comment"
            loading={loading}
            saving={saving}
            content={content} setContent={setContent}
            reason={reason} setReason={setReason}
            onSave={handleSave}
        />
    );
})``;

export default null;
